import {
	Button, ButtonTypes, Card, DatePicker, Input, type SelectOption, Modal, Select, SettingsLayout, Switch,
	usePropsChanged
} from "@clintonelec/react-storybook";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import "Components/ConfigLayout/System/DateTimeSettings/DateTimeSettings.less";
import { DateFormat, TimeFormat, TimezoneDst } from "Data/Objects/System";
import { selectDateTime, setDateTimeAction } from "Data/Redux/Slices/Settings/System/DateTime";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { produce } from "immer";
import type { DateTimeSettingsState } from "Interfaces";
import { isEqual } from "lodash";
import { type Dispatch, type FormEvent, memo, type SetStateAction, useEffect, useState } from "react";

const dateFormatOptions: SelectOption[] = [
	{
		label: DateFormat.YYYY_MM_DD,
		value: DateFormat.YYYY_MM_DD
	},
	{
		label: DateFormat.MM_DD_YYYY,
		value: DateFormat.MM_DD_YYYY
	},
	{
		label: DateFormat.DD_MM_YYYY,
		value: DateFormat.DD_MM_YYYY
	}
];

const timeFormatOptions: SelectOption[] = [
	{
		label: TimeFormat.TWENTYFOUR_HOUR,
		value: TimeFormat.TWENTYFOUR_HOUR
	},
	{
		label: TimeFormat.TWELVE_HOUR,
		value: TimeFormat.TWELVE_HOUR
	}
];

const syncTimeOptions: SelectOption[] = new Array(24).fill(0).map((current, index) => {
	return {
		label: `${ index.toString().padStart(2, "0") }:00`,
		value: `${ index.toString().padStart(2, "0") }:00`
	};
});

const timezoneDstOptions: SelectOption[] = Object.values(TimezoneDst).map(value => {
	return {
		label: value,
		value: value
	};
});

const datePickerDateFormat = {
	[ DateFormat.YYYY_MM_DD ]: "yyyy/MM/dd",
	[ DateFormat.MM_DD_YYYY ]: "MM/dd/yyyy",
	[ DateFormat.DD_MM_YYYY ]: "dd/MM/yyyy"
};

const datePickerTimeFormat = {
	[ TimeFormat.TWENTYFOUR_HOUR ]: "HH:mm:ss",
	[ TimeFormat.TWELVE_HOUR ]: "hh:mm:ss aa"
};

type DateTimePickerProps = {
	format: string;
	dateTimeSettings: DateTimeSettingsState;
	setDiff: Dispatch<SetStateAction<DateTimeSettingsState>>;
	timeFormat: TimeFormat;
};

function DateTimePicker(props: DateTimePickerProps) {
	const { format, dateTimeSettings, setDiff, timeFormat } = props;
	const newDateTime = dateTimeSettings.dateTimeSetup.dateTime;
	const [ dateTime, setDateTime ] = useState(new Date(newDateTime));
	const [ isCalendarOpen, setIsCalendarOpen ] = useState(false);
	const handleCalendarClose = () => setIsCalendarOpen(false);
	const handleCalendarOpen = () => setIsCalendarOpen(true);

	useEffect(() => {
		if (!isCalendarOpen) {
			const timeout = setTimeout(() => {
				setDateTime(new Date(dateTime.getTime() + 1000));
			}, 1000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [ dateTime, isCalendarOpen ]);

	usePropsChanged(newDateTime, () => {
		setDateTime(new Date(newDateTime));
	});

	const handleOk = (date: Date) => {
		setDiff(produce(dateTimeSettings, draft => {
			draft.dateTimeSetup.dateTime = date.getTime();
		}));
	};

	return (
		<div className="date-time-setup-picker form-input">
			<DatePicker
				cleanable={ false }
				format={ format }
				onClose={ handleCalendarClose }
				onOk={ handleOk }
				onOpen={ handleCalendarOpen }
				showMeridian={ timeFormat === TimeFormat.TWELVE_HOUR }
				size="sm"
				value={ dateTime }
			/>
		</div>
	);
}

function DateTimeSettings() {
	const dispatch = useAppDispatch();
	const dateTimeSettings = useAppSelector(selectDateTime);
	const [ localDateTimeSettings, setLocalDateTimeSettings ] = useState(dateTimeSettings);
	const {
		dateTimeSetup: { dateFormat, timeFormat },
		networkTimeServerSetup: { timeServer, autoTimeSync, syncTime },
		timezoneDstSetup: { timezoneDst, dst }
	} = localDateTimeSettings;

	const setDateTimeSettings = () => dispatch(setDateTimeAction(localDateTimeSettings));
	const disabled = isEqual(localDateTimeSettings, dateTimeSettings);
	const format = datePickerDateFormat[ dateFormat ] + " " + datePickerTimeFormat[ timeFormat ];
	const [ syncPcModalVisible, setSyncPcModalVisible ] = useState(false);
	const [ syncServerModalVisible, setSyncServerModalVisible ] = useState(false);
	const handleSyncPcVisibilityChange = () => setSyncPcModalVisible(visible => !visible);
	const handleSyncServerVisibilityChange = () => setSyncServerModalVisible(visible => !visible);

	const handleReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setLocalDateTimeSettings(dateTimeSettings);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setDateTimeSettings();
	};

	const handleDateFormatSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.dateTimeSetup.dateFormat = value as DateFormat;
		}));
	};

	const handleTimeFormatSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.dateTimeSetup.timeFormat = value as TimeFormat;
		}));
	};

	const handleTimeServerUpdate = (value: string) => {
		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.networkTimeServerSetup.timeServer = value;
		}));
	};

	const handleAutoTimeSyncChange = (checked: boolean) => {
		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.networkTimeServerSetup.autoTimeSync = checked;
		}));
	};

	const handleSyncTimeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.networkTimeServerSetup.syncTime = value;
		}));
	};

	const handleTimezoneDstSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.timezoneDstSetup.timezoneDst = value as TimezoneDst;
		}));
	};

	const handleDstChange = (checked: boolean) => {
		setLocalDateTimeSettings(produce(localDateTimeSettings, draft => {
			draft.timezoneDstSetup.dst = checked;
		}));
	};

	return (
		<SettingsLayout.Form className="date-time-settings" onReset={ handleReset } onSubmit={ handleSubmit }>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="Date Time Setup">
					<div className="form-row">
						<span>Date Time</span>
						<DateTimePicker
							format={ format }
							dateTimeSettings={ localDateTimeSettings }
							setDiff={ setLocalDateTimeSettings }
							timeFormat={ timeFormat }
						/>
					</div>
					<div className="form-row">
						<span>Date Format</span>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleDateFormatSelect }
							options={ dateFormatOptions }
							value={ dateFormat }
						/>
					</div>
					<div className="form-row">
						<span>Time Format</span>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleTimeFormatSelect }
							options={ timeFormatOptions }
							value={ timeFormat }
						/>
					</div>
					<div className="form-row">
						<Modal
							className="date-time-sync-modal"
							modalContent="The system time is changing"
							onVisibilityChange={ handleSyncPcVisibilityChange }
							title="Date Time Setup"
							visible={ syncPcModalVisible }
							width={ 400 }
						>
							<Button
								className="form-button"
								htmlType="button"
								type={ ButtonTypes.TERTIARY }
							>
								Sync with PC
							</Button>
						</Modal>
					</div>
				</Card>
				<Card size="small" title="Network Time Server Setup">
					<div className="form-row">
						<span>Time Server</span>
						<Input
							onUpdate={ handleTimeServerUpdate }
							value={ timeServer }
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row switch">
						<span>Auto Time Sync</span>
						<Switch
							onChange={ handleAutoTimeSyncChange }
							checked={ autoTimeSync }
						/>
					</div>
					<div className="form-row">
						<span>Sync Time</span>
						<Select
							allowClear={ false }
							className="form-select"
							disabled={ !autoTimeSync }
							onSelect={ handleSyncTimeSelect }
							options={ syncTimeOptions }
							value={ syncTime }
						/>
					</div>
					<div className="form-row">
						<Modal
							className="date-time-sync-modal"
							modalContent="System time is synchronized with NTP server"
							onVisibilityChange={ handleSyncServerVisibilityChange }
							title="Date Time Setup"
							visible={ syncServerModalVisible }
							width={ 400 }
						>
							<Button
								className="form-button"
								htmlType="button"
								type={ ButtonTypes.TERTIARY }
							>
								Sync Now
							</Button>
						</Modal>
					</div>
				</Card>
				<Card size="small" title="Timezone / DST">
					<div className="form-row">
						<span>Timezone / DST</span>
						<Select
							allowClear={ false }
							className="form-select"
							onSelect={ handleTimezoneDstSelect }
							options={ timezoneDstOptions }
							value={ timezoneDst }
						/>
					</div>
					<div className="form-row switch">
						<span>DST</span>
						<Switch
							onChange={ handleDstChange }
							checked={ dst }
						/>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(DateTimeSettings);
